import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import fr from 'facet-rest';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import {
  useTabContentContext,
} from 'Views/Page/Content/Templates/WhatIfTemplate/Content/RightSide/BottomPart/DetailPicker/Details/Context';
import FacetTabs
  from 'Views/Page/Content/Templates/WhatIfTemplate/Content/RightSide/BottomPart/Tabs';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';
import ChartActionsPicker
  from 'Views/Page/Content/Templates/WhatIfTemplate/Content/RightSide/BottomPart/AllocationAnalysisActions';
import { useFilterContext } from 'Views/Page/Content/Templates/WhatIfTemplate/Content/RightSide/FilterContext';
import AddIcon from '@material-ui/icons/Add';
import FullyRoundedButton from 'Components/ComponentLibrary/Components/Uncategorized/FullyRoundedButton';
import useEventTrackingLogEvent, { categories, eventTypes } from 'EventTracking/useEventTrackingLogEvent';
import TimeframeOptions
  from 'Views/Page/Content/Templates/WhatIfTemplate/Content/RightSide/BottomPart/DetailPicker/Details/Content/ActionBar/TimeframeOptions';
import { CURRENT_TIMEFRAME, TIMEFRAME_QP } from 'Components/ComponentLibrary/Organisms/ScenarioChart';

const styles = theme => ({
  root: {
    borderBottom: `1px solid ${theme.palette.facet.mediumGray}`,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: theme.spacing(2),
    padding: theme.spacing(0, 4),
    position: 'relative',
  },
  action: {
    color: theme.palette.facet.accentGreen,
    fontWeight: theme.typography.fontWeightBold,
    textTransform: 'uppercase',
    flexShrink: 0,
  },
  timeframeSelector: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 'auto',
  },
  chartActions: {
    position: 'absolute',
    width: '100%',
    top: 0,
    left: 0,
    transform: 'translateY(-100%)',
  },
  addOutcomeButton: {
    flexShrink: 0,
  },
  goToCurrentButton: {
    marginRight: theme.spacing(1),
  },
});

const ActionBar = ({ className, classes }) => {
  const { logStandardizedEvent } = useEventTrackingLogEvent();
  const { allocationsTimeframe, setAllocationsTimeframe, isTimeFrameLoading } = useFilterContext();
  const { pageContentResource } = usePageContentContext();

  const url = new URL(pageContentResource.link('self').attr('url'));
  const timeframe = url.searchParams.get(TIMEFRAME_QP);

  // set initial timeframe from URL
  const [allocationTableTimeframe, setAllocationTableTimeframe] = useState(timeframe);

  const setNewTimeframe = (newTimeFrame) => {
    setAllocationsTimeframe(newTimeFrame);
    setAllocationTableTimeframe(newTimeFrame);
  };

  const {
    activeTabResourceType,
    setActiveTabResourceType,
    timeFrameOptions,
    tabs,
  } = useTabContentContext();

  const { loadModalCollectionByLink } = useModalContext();
  const handleActionClick = () => {
    loadModalCollectionByLink(
      pageContentResource
        .relationship(fr.relationships.facetPlanViewAddOutcome.name)
        .link('related')
        .url(),
    );
    logStandardizedEvent(categories.whatIf, eventTypes.clickButton, 'add outcome', {
      eventProperties: {
        scenarioId: pageContentResource.id(),
      },
    });
  };

  const hasNextPage = !!pageContentResource?.link('next');
  const hasPrevPage = !!pageContentResource?.link('prev');

  const getPrevPage = React.useCallback(async () => {
    if (hasPrevPage) {
      const prevUrl = new URL(pageContentResource.link('prev').url());
      const newTimeframe = prevUrl.searchParams.get(TIMEFRAME_QP);
      if (newTimeframe) {
        setNewTimeframe(newTimeframe);
      }
    }
  }, [hasPrevPage, setNewTimeframe]);

  const getNextPage = React.useCallback(async () => {
    if (hasNextPage) {
      const nextUrl = new URL(pageContentResource.link('next').url());
      const newTimeframe = nextUrl.searchParams.get(TIMEFRAME_QP);
      if (newTimeframe) {
        setNewTimeframe(newTimeframe);
      }
    }
  }, [hasNextPage, setNewTimeframe]);

  const renderTimeframeOptions = React.useCallback(() => {
    const hasTimeFrameOptions = Boolean(timeFrameOptions && timeFrameOptions.length >= 1);
    const hideTimeframeOptions = activeTabResourceType === 'scenarioDetails';
    if (!hasTimeFrameOptions || hideTimeframeOptions) {
      return null;
    }

    // Reset timeframe to 'Current'
    const handleSetTimeframeToCurrent = () => setNewTimeframe(CURRENT_TIMEFRAME);

    // handle select value change
    const handleOnChange = event => setNewTimeframe(event.value);

    return (
      <TimeframeOptions
        timeFrameOptions={timeFrameOptions}
        isLoading={isTimeFrameLoading}
        nextOption={hasNextPage}
        prevOption={hasPrevPage}
        handleSetTimeframeToCurrent={handleSetTimeframeToCurrent}
        handleOnChange={handleOnChange}
        handleOnNext={getNextPage}
        handleOnPrev={getPrevPage}
        allocationTableTimeframe={allocationTableTimeframe}
        setAllocationTableTimeframe={setAllocationTableTimeframe}
        timeFrame={allocationsTimeframe}
        setAllocationsTimeframe={setAllocationsTimeframe}
      />
    );
  }, [pageContentResource, timeFrameOptions, allocationTableTimeframe, isTimeFrameLoading]);

  return (
    <Box className={classNames(classes.root, className)}>
      <Box className={classNames(classes.chartActions)}>
        <ChartActionsPicker />
      </Box>
      <div className={classes.addOutcomeButton}>
        {pageContentResource
          .relationship(fr.relationships.facetPlanViewAddOutcome.name)
          ? (
            <FullyRoundedButton
              onClick={handleActionClick}
              test={'facet-plan-view-add-outcome-button'}
            >
              <AddIcon fontSize={'inherit'} />
              {'Add Outcome'}
            </FullyRoundedButton>
          )
          : null}
      </div>
      <FacetTabs
        onChange={(e, value) => setActiveTabResourceType(value)}
        value={activeTabResourceType}
        tabs={tabs}
        variant={'scrollable'}
      />
      {renderTimeframeOptions()}
    </Box>
  );
};

ActionBar.defaultProps = {
  className: '',
};

ActionBar.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(ActionBar);
